import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { useSession } from '@/shared/hooks/useSession'
import { useParams } from 'react-router-dom'
import type {
  InstallationFuelDto,
  InstallationOtherMaterialCategoryDto,
  InstallationPrecursorCnCodeDto,
  InstallationPrecursorSupplyOrigin,
  InstallationProducedCnCodeDto,
  InstallationScrapCategoryDto,
  OperatorConfigDto,
} from '@/features/installations/api/installation-configuration-model'

export const OPERATOR_CONFIG_QUERY_KEY = 'operator-config'

export const useOperatorConfig = () => {
  return useQuery({
    queryKey: [OPERATOR_CONFIG_QUERY_KEY],
    queryFn: async () => {
      const res = await apiClient.get<OperatorConfigDto>('/v1/config')
      return res.data
    },
    staleTime: 60 * 60 * 1000,
  })
}

const sessionPath = (
  organizationId: string | undefined,
  installationId: string | undefined
) =>
  organizationId && installationId
    ? `/v1/organizations/${organizationId}/installations/${installationId}`
    : null

export const installationPrecursorCnCodesQueryKey = (
  organizationId: string | undefined,
  installationId: string | undefined
) => ['installation-precursor-cn-codes', organizationId, installationId] as const

export const installationProducedCnCodesQueryKey = (
  organizationId: string | undefined,
  installationId: string | undefined
) => ['installation-produced-cn-codes', organizationId, installationId] as const

export const installationFuelsQueryKey = (
  organizationId: string | undefined,
  installationId: string | undefined
) => ['installation-fuels', organizationId, installationId] as const

export const installationScrapCategoriesQueryKey = (
  organizationId: string | undefined,
  installationId: string | undefined
) => ['installation-scrap-categories', organizationId, installationId] as const

export const installationOtherMaterialCategoriesQueryKey = (
  organizationId: string | undefined,
  installationId: string | undefined
) =>
  ['installation-other-material-categories', organizationId, installationId] as const

function useInstallationSessionPath() {
  const { installationId } = useParams<{ installationId: string }>()
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()
  return { organizationId, installationId, basePath: sessionPath(organizationId, installationId) }
}

export const useInstallationPrecursorCnCodesQuery = () => {
  const { organizationId, installationId, basePath } = useInstallationSessionPath()

  return useQuery({
    queryKey: installationPrecursorCnCodesQueryKey(organizationId, installationId),
    queryFn: async () => {
      const res = await apiClient.get<{ precursorCnCodes: InstallationPrecursorCnCodeDto[] }>(
        `${basePath}/precursor-cn-codes`
      )
      return res.data.precursorCnCodes
    },
    enabled: Boolean(basePath),
  })
}

export const useInstallationProducedCnCodesQuery = () => {
  const { organizationId, installationId, basePath } = useInstallationSessionPath()

  return useQuery({
    queryKey: installationProducedCnCodesQueryKey(organizationId, installationId),
    queryFn: async () => {
      const res = await apiClient.get<{ producedCnCodes: InstallationProducedCnCodeDto[] }>(
        `${basePath}/produced-cn-codes`
      )
      return res.data.producedCnCodes
    },
    enabled: Boolean(basePath),
  })
}

export const cbamGoodsCodeLookupQueryKey = (goodsCode8Digits: string) =>
  ['config-goods-by-code', goodsCode8Digits] as const

export type CbamGoodsCodeDto = {
  goodsCode8Digits: string
}

export const cbamGoodsCodesQueryKey = ['config-goods'] as const

export const useCbamGoodsCodesQuery = (options: { enabled: boolean }) => {
  return useQuery({
    queryKey: cbamGoodsCodesQueryKey,
    queryFn: async () => {
      const res = await apiClient.get<CbamGoodsCodeDto[]>('/v1/config/goods')
      return res.data
    },
    enabled: options.enabled,
    staleTime: 60 * 60 * 1000,
  })
}

const producedCnCode8DigitsPattern = /^\d{8}$/

/** Uses GET /v1/config/goods/{code}: 200 = in CBAM goods list, 404 = not listed. */
export const useCbamGoodsCodeLookupQuery = (options: {
  goodsCode8Digits: string
  enabled: boolean
}) => {
  const trimmed = options.goodsCode8Digits.trim()
  const valid = producedCnCode8DigitsPattern.test(trimmed)
  const enabled = options.enabled && valid

  return useQuery({
    queryKey: cbamGoodsCodeLookupQueryKey(trimmed),
    queryFn: async () => {
      const res = await apiClient.get<unknown>(
        `/v1/config/goods/${encodeURIComponent(trimmed)}`,
        { validateStatus: (status) => status === 200 || status === 404 }
      )
      return { cbamRelevant: res.status === 200 }
    },
    enabled,
  })
}

export const useInstallationFuelsQuery = () => {
  const { organizationId, installationId, basePath } = useInstallationSessionPath()

  return useQuery({
    queryKey: installationFuelsQueryKey(organizationId, installationId),
    queryFn: async () => {
      const res = await apiClient.get<{ fuels: InstallationFuelDto[] }>(`${basePath}/fuels`)
      return res.data.fuels
    },
    enabled: Boolean(basePath),
  })
}

export const useInstallationScrapCategoriesQuery = () => {
  const { organizationId, installationId, basePath } = useInstallationSessionPath()

  return useQuery({
    queryKey: installationScrapCategoriesQueryKey(organizationId, installationId),
    queryFn: async () => {
      const res = await apiClient.get<{ scrapCategories: InstallationScrapCategoryDto[] }>(
        `${basePath}/scrap-categories`
      )
      return res.data.scrapCategories
    },
    enabled: Boolean(basePath),
  })
}

export const useInstallationOtherMaterialCategoriesQuery = () => {
  const { organizationId, installationId, basePath } = useInstallationSessionPath()

  return useQuery({
    queryKey: installationOtherMaterialCategoriesQueryKey(organizationId, installationId),
    queryFn: async () => {
      const res = await apiClient.get<{
        otherMaterialCategories: InstallationOtherMaterialCategoryDto[]
      }>(`${basePath}/other-material-categories`)
      return res.data.otherMaterialCategories
    },
    enabled: Boolean(basePath),
  })
}

export const useInstallationPrecursorCnCodeMutations = () => {
  const queryClient = useQueryClient()
  const { organizationId, installationId, basePath } = useInstallationSessionPath()
  const key = installationPrecursorCnCodesQueryKey(organizationId, installationId)

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: key })
  }

  const addPrecursorCnCode = useMutation({
    mutationFn: async (payload: {
      cnCode: string
      supplyOrigin: InstallationPrecursorSupplyOrigin
    }) => {
      await apiClient.post(`${basePath}/precursor-cn-codes`, payload)
    },
    onSuccess: invalidate,
  })

  const patchPrecursorCnSupplyOrigin = useMutation({
    mutationFn: async (payload: {
      itemId: string
      supplyOrigin: InstallationPrecursorSupplyOrigin
    }) => {
      await apiClient.patch(
        `${basePath}/precursor-cn-codes/${payload.itemId}`,
        { supplyOrigin: payload.supplyOrigin }
      )
    },
    onSuccess: invalidate,
  })

  const removePrecursorCnCode = useMutation({
    mutationFn: async (itemId: string) => {
      await apiClient.delete(`${basePath}/precursor-cn-codes/${itemId}`)
    },
    onSuccess: invalidate,
  })

  return { addPrecursorCnCode, patchPrecursorCnSupplyOrigin, removePrecursorCnCode }
}

export const useInstallationProducedCnCodeMutations = () => {
  const queryClient = useQueryClient()
  const { organizationId, installationId, basePath } = useInstallationSessionPath()
  const key = installationProducedCnCodesQueryKey(organizationId, installationId)

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: key })
  }

  const addProducedCnCode = useMutation({
    mutationFn: async (cnCode: string) => {
      await apiClient.post(`${basePath}/produced-cn-codes`, { cnCode })
    },
    onSuccess: invalidate,
  })

  const removeProducedCnCode = useMutation({
    mutationFn: async (itemId: string) => {
      await apiClient.delete(`${basePath}/produced-cn-codes/${itemId}`)
    },
    onSuccess: invalidate,
  })

  return { addProducedCnCode, removeProducedCnCode }
}

export const useInstallationFuelMutations = () => {
  const queryClient = useQueryClient()
  const { organizationId, installationId, basePath } = useInstallationSessionPath()
  const key = installationFuelsQueryKey(organizationId, installationId)

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: key })
  }

  const addFuel = useMutation({
    mutationFn: async (fuelTypeId: string) => {
      await apiClient.post(`${basePath}/fuels`, { fuelTypeId })
    },
    onSuccess: invalidate,
  })

  const removeFuel = useMutation({
    mutationFn: async (itemId: string) => {
      await apiClient.delete(`${basePath}/fuels/${itemId}`)
    },
    onSuccess: invalidate,
  })

  return { addFuel, removeFuel }
}

export const useInstallationScrapCategoryMutations = () => {
  const queryClient = useQueryClient()
  const { organizationId, installationId, basePath } = useInstallationSessionPath()
  const key = installationScrapCategoriesQueryKey(organizationId, installationId)

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: key })
  }

  const addScrapCategory = useMutation({
    mutationFn: async (name: string) => {
      await apiClient.post(`${basePath}/scrap-categories`, { name })
    },
    onSuccess: invalidate,
  })

  const patchScrapCategory = useMutation({
    mutationFn: async ({ itemId, name }: { itemId: string; name: string }) => {
      await apiClient.patch(`${basePath}/scrap-categories/${itemId}`, { name })
    },
    onSuccess: invalidate,
  })

  const removeScrapCategory = useMutation({
    mutationFn: async (itemId: string) => {
      await apiClient.delete(`${basePath}/scrap-categories/${itemId}`)
    },
    onSuccess: invalidate,
  })

  return { addScrapCategory, patchScrapCategory, removeScrapCategory }
}

export const useInstallationOtherMaterialCategoryMutations = () => {
  const queryClient = useQueryClient()
  const { organizationId, installationId, basePath } = useInstallationSessionPath()
  const key = installationOtherMaterialCategoriesQueryKey(organizationId, installationId)

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: key })
  }

  const addOtherMaterialCategory = useMutation({
    mutationFn: async (name: string) => {
      await apiClient.post(`${basePath}/other-material-categories`, { name })
    },
    onSuccess: invalidate,
  })

  const patchOtherMaterialCategory = useMutation({
    mutationFn: async ({ itemId, name }: { itemId: string; name: string }) => {
      await apiClient.patch(`${basePath}/other-material-categories/${itemId}`, { name })
    },
    onSuccess: invalidate,
  })

  const removeOtherMaterialCategory = useMutation({
    mutationFn: async (itemId: string) => {
      await apiClient.delete(`${basePath}/other-material-categories/${itemId}`)
    },
    onSuccess: invalidate,
  })

  return {
    addOtherMaterialCategory,
    patchOtherMaterialCategory,
    removeOtherMaterialCategory,
  }
}
